import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  RefreshCw,
  Mail,
  Phone,
  Activity as AiNotesLogoIcon,
  Sparkles,
  ScanText,
  Layers,
  Database
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const RUN_ANALYSIS_TOAST_ID = 'notes-ai-run';

export default function AnalisiNoteAI() {
  const { user: authUser, loading: authLoading } = useAuth();
  const roleName = (authUser?.role_name || authUser?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const y = past.getFullYear();
    const m = String(past.getMonth() + 1).padStart(2, '0');
    const d = String(past.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [legalOptions, setLegalOptions] = useState([]);
  const [selectedLegal, setSelectedLegal] = useState('');
  const [onlyLegalNotes, setOnlyLegalNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tab, setTab] = useState('notes');
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [expandedAnalysisRow, setExpandedAnalysisRow] = useState(null);
  const [notesTabAnalysisPresenceFilter, setNotesTabAnalysisPresenceFilter] = useState('without');
  const [analysisPresenceFilter, setAnalysisPresenceFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [runInProgress, setRunInProgress] = useState(false);
  const [runInfoOpen, setRunInfoOpen] = useState(false);
  const runLockRef = useRef(false);
  const runPollIntervalRef = useRef(null);
  const initialLoadRef = useRef(false);

  const stopRunPolling = () => {
    if (runPollIntervalRef.current) {
      clearInterval(runPollIntervalRef.current);
      runPollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopRunPolling();
    };
  }, []);

  const loadLegalOptions = async () => {
    try {
      const res = await api.get('/lorenzo-users');
      const users = res.data || [];
      const sorted = [...users].sort((a, b) => {
        const nameA = [a.name, a.surname].filter(Boolean).join(' ') || '';
        const nameB = [b.name, b.surname].filter(Boolean).join(' ') || '';
        return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
      });
      setLegalOptions(sorted);
    } catch (error) {
      console.error('Errore nel caricamento referenti legali per Analisi Note AI:', error);
    }
  };

  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const loadNotes = async ({
    withNotification = false,
    pageToLoad = page,
    rowsPerPageToUse = rowsPerPage,
    clearSelection = false
  } = {}) => {
    let toastId = null;
    if (withNotification) {
      toastId = toast.info('Caricamento Analisi AI note in corso...', {
        autoClose: false
      });
    }
    setLoading(true);
    try {
      const params = {
        from: fromDate || '',
        to: toDate || '',
        limit: rowsPerPageToUse,
        offset: pageToLoad * rowsPerPageToUse,
        only_legal_notes: onlyLegalNotes ? 'true' : 'false'
      };
      if (selectedLegal) {
        params.rif_responsabile_legale = selectedLegal;
      }
      if (tab === 'analyzed') {
        if (analysisPresenceFilter !== 'all') {
          params.analysis_presence = analysisPresenceFilter;
        }
        if (riskFilter !== 'all') {
          params.risk = riskFilter;
        }
        if (qualityFilter !== 'all') {
          params.quality = qualityFilter;
        }
      } else if (tab === 'notes') {
        if (notesTabAnalysisPresenceFilter !== 'all') {
          params.analysis_presence = notesTabAnalysisPresenceFilter;
        }
      }
      const res = await api.get('/notes-ai', { params });
      const payload = res.data || {};
      setNotes(payload.notes || []);
      setTotal(payload.total || 0);
      setPage(pageToLoad);
      if (clearSelection) {
        setSelectedNoteIds([]);
        setExpandedAnalysisRow(null);
      }
      if (withNotification && toastId) {
        toast.update(toastId, {
          render: 'Analisi AI note caricata',
          type: 'success',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Errore nel caricamento Analisi AI note:', error);
      if (withNotification && toastId) {
        toast.update(toastId, {
          render: 'Errore durante il caricamento Analisi AI note',
          type: 'error',
          autoClose: 5000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const params = {
        from: fromDate || '',
        to: toDate || '',
        only_legal_notes: onlyLegalNotes ? 'true' : 'false'
      };
      if (selectedLegal) {
        params.rif_responsabile_legale = selectedLegal;
      }
      const res = await api.get('/notes-ai/summary', { params });
      const payload = res.data || {};
      setSummaryData({
        totalAnalyzed: payload.totalAnalyzed || 0,
        byOutcome: payload.byOutcome || [],
        byRisk: payload.byRisk || [],
        byQuality: payload.byQuality || []
      });
    } catch (error) {
      console.error('Errore nel caricamento riepilogo Analisi AI note:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;

    if (isAdmin) {
      loadLegalOptions();
      return;
    }

    const currentOption = {
      id: authUser?.id,
      name: authUser?.name || '',
      surname: authUser?.surname || ''
    };
    setLegalOptions([currentOption]);
    setSelectedLegal(authUser?.id != null ? String(authUser.id) : '');
  }, [authLoading, authUser, isAdmin]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    if (authLoading) return;
    if (!authUser) return;
    if (!isAdmin && !selectedLegal) return;
    loadNotes({ pageToLoad: 0, clearSelection: true });
    initialLoadRef.current = true;
  }, [authLoading, authUser, isAdmin, selectedLegal]);

  useEffect(() => {
    if (authLoading || !authUser) return;
    if (!isAdmin && !selectedLegal) return;
    if (tab === 'analyzed') {
      loadNotes({ pageToLoad: 0, clearSelection: true });
    }
  }, [analysisPresenceFilter, riskFilter, qualityFilter, tab]);

  useEffect(() => {
    if (authLoading || !authUser) return;
    if (!isAdmin && !selectedLegal) return;
    if (tab === 'notes') {
      loadNotes({ pageToLoad: 0, clearSelection: true });
    }
  }, [notesTabAnalysisPresenceFilter, tab]);

  useEffect(() => {
    if (authLoading || !authUser) return;
    if (!isAdmin && !selectedLegal) return;
    if (tab === 'summary') {
      loadSummary();
    }
  }, [tab, fromDate, toDate, selectedLegal, onlyLegalNotes]);

  const handleApply = () => {
    loadNotes({ withNotification: true, pageToLoad: 0, clearSelection: true });
  };

  const handleOpenRunInfo = () => {
    if (runLockRef.current || runInProgress) {
      toast.info('Analisi già in corso', {
        toastId: RUN_ANALYSIS_TOAST_ID,
        autoClose: 2500
      });
      return;
    }

    if (tab === 'notes') {
      if (!selectedNoteIds.length) {
        toast.warn('Seleziona almeno una nota da analizzare');
        return;
      }
    }

    setRunInfoOpen(true);
  };

  const handleRunAnalysis = async () => {
    let keepClientLock = false;
    if (runLockRef.current || runInProgress) {
      toast.info('Analisi già in corso', {
        toastId: RUN_ANALYSIS_TOAST_ID,
        autoClose: 2500
      });
      return;
    }

    if (tab === 'notes') {
      if (!selectedNoteIds.length) {
        toast.warn('Seleziona almeno una nota da analizzare');
        return;
      }
    }

    runLockRef.current = true;
    toast.info('Analisi AI note avviata...', {
      toastId: RUN_ANALYSIS_TOAST_ID,
      autoClose: false
    });
    try {
      const payload = {
        from: fromDate || '',
        to: toDate || '',
        only_legal_notes: onlyLegalNotes
      };
      if (selectedLegal) {
        payload.rif_responsabile_legale = selectedLegal;
      }
      if (tab === 'notes' && selectedNoteIds.length) {
        payload.note_ids = selectedNoteIds;
      }
      const res = await api.post('/notes-ai/run', payload);
      const job = (res.data && res.data.job) || null;
      const startedJobId = job && job.id ? String(job.id) : null;
      if (!startedJobId) {
        throw new Error('Job ID non ricevuto dal server');
      }

      setRunInProgress(true);
      keepClientLock = true;
      toast.update(RUN_ANALYSIS_TOAST_ID, {
        render: 'Analisi AI note avviata. Elaborazione in background...',
        type: 'info',
        autoClose: false
      });

      const pollStatus = async () => {
        try {
          const statusRes = await api.get(`/notes-ai/run/status/${startedJobId}`);
          const statusJob = (statusRes.data && statusRes.data.job) || null;
          if (!statusJob) {
            return;
          }

          if (statusJob.status === 'queued' || statusJob.status === 'running') {
            const progress = statusJob.progress || {};
            const processedBatches = progress.processedBatches || 0;
            const totalBatches = progress.totalBatches || 0;
            const analyzed = progress.analyzed || 0;
            const totalCandidates = progress.totalCandidates || 0;
            const batchInfo =
              totalBatches > 0
                ? `Batch ${processedBatches}/${totalBatches}`
                : 'Preparazione batch...';
            const analyzedInfo =
              totalCandidates > 0
                ? ` ${analyzed}/${totalCandidates} note`
                : '';
            toast.update(RUN_ANALYSIS_TOAST_ID, {
              render: `Analisi in corso... ${batchInfo}${analyzedInfo}`,
              type: 'info',
              autoClose: false
            });
            return;
          }

          stopRunPolling();
          setRunInProgress(false);
          runLockRef.current = false;
          keepClientLock = false;
          if (statusJob.status === 'completed') {
            const result = statusJob.result || {};
            toast.update(RUN_ANALYSIS_TOAST_ID, {
              render: `Analisi completata: ${result.analyzed || 0} note su ${result.totalCandidates || 0}`,
              type: 'success',
              autoClose: 6000
            });
            await loadNotes({ pageToLoad: 0, clearSelection: true });
            if (tab === 'summary') {
              await loadSummary();
            }
            return;
          }

          const errorMessage =
            (statusJob.error && statusJob.error.message) ||
            'Errore durante l’Analisi AI note';
          toast.update(RUN_ANALYSIS_TOAST_ID, {
            render: errorMessage,
            type: 'error',
            autoClose: 6000
          });
        } catch (pollError) {
          const pollMessage =
            pollError &&
            pollError.response &&
            pollError.response.data &&
            pollError.response.data.message
              ? pollError.response.data.message
              : null;
          if (pollMessage) {
            toast.update(RUN_ANALYSIS_TOAST_ID, {
              render: `Stato analisi non disponibile: ${pollMessage}`,
              type: 'warning',
              autoClose: 3500
            });
          }
        }
      };

      stopRunPolling();
      await pollStatus();
      runPollIntervalRef.current = setInterval(pollStatus, 2500);
    } catch (error) {
      console.error('Errore durante Analisi AI note:', error);
      const statusCode = error && error.response ? error.response.status : null;
      const backendMessage =
        error &&
        error.response &&
        error.response.data &&
        typeof error.response.data.message === 'string'
          ? error.response.data.message
          : null;

      if (statusCode === 409) {
        const activeJob =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.activeJob
            ? error.response.data.activeJob
            : null;
        const activeJobId = activeJob && activeJob.id ? String(activeJob.id) : null;
        if (activeJobId) {
          setRunInProgress(true);
          keepClientLock = true;
          toast.update(RUN_ANALYSIS_TOAST_ID, {
            render: 'Analisi già in corso. Mi aggancio al job attivo...',
            type: 'info',
            autoClose: false
          });
          stopRunPolling();
          runPollIntervalRef.current = setInterval(async () => {
            try {
              const statusRes = await api.get(`/notes-ai/run/status/${activeJobId}`);
              const statusJob = (statusRes.data && statusRes.data.job) || null;
              if (!statusJob) {
                return;
              }
              if (statusJob.status === 'queued' || statusJob.status === 'running') {
                return;
              }
              stopRunPolling();
              setRunInProgress(false);
              runLockRef.current = false;
              keepClientLock = false;
              if (statusJob.status === 'completed') {
                const result = statusJob.result || {};
                toast.update(RUN_ANALYSIS_TOAST_ID, {
                  render: `Analisi completata: ${result.analyzed || 0} note su ${result.totalCandidates || 0}`,
                  type: 'success',
                  autoClose: 6000
                });
                await loadNotes({ pageToLoad: 0, clearSelection: true });
              } else {
                toast.update(RUN_ANALYSIS_TOAST_ID, {
                  render:
                    (statusJob.error && statusJob.error.message) ||
                    'Errore durante l’Analisi AI note',
                  type: 'error',
                  autoClose: 6000
                });
              }
            } catch (_) {
            }
          }, 2500);
          return;
        }
      }

      setRunInProgress(false);
      runLockRef.current = false;
      keepClientLock = false;
      toast.update(RUN_ANALYSIS_TOAST_ID, {
        render: backendMessage || 'Errore durante l’Analisi AI note',
        type: 'error',
        autoClose: 5000
      });
    } finally {
      if (!keepClientLock) {
        runLockRef.current = false;
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadNotes({ pageToLoad: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 25;
    setRowsPerPage(value);
    setPage(0);
    loadNotes({ pageToLoad: 0, rowsPerPageToUse: value, clearSelection: true });
  };

  const handleToggleNoteSelection = (noteId) => {
    setSelectedNoteIds((prev) => {
      if (prev.includes(noteId)) {
        return prev.filter((id) => id !== noteId);
      }
      return [...prev, noteId];
    });
  };

  const handleToggleSelectAllCurrentPage = () => {
    const currentPageIds = filteredNotesForSelectionTab.map((n) => n.note_id);
    const allSelected = currentPageIds.every((id) => selectedNoteIds.includes(id));
    if (allSelected) {
      setSelectedNoteIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      setSelectedNoteIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    }
  };

  const handleToggleAnalysisRow = (noteId) => {
    if (!noteId) return;
    setExpandedAnalysisRow((prev) => (prev === noteId ? null : noteId));
  };

  const filteredAnalyzedNotes = notes;
  const filteredNotesForSelectionTab = notes;
  const notesWithAnalysisCount = notes.filter((row) => !!row.analysis).length;
  const notesWithoutAnalysisCount = notes.length - notesWithAnalysisCount;

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={runInfoOpen} onClose={() => setRunInfoOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Sparkles size={18} color="#4f46e5" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Analisi AI Note
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Analisi qualitativa del testo delle note lavorate
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.2
                }}
              >
                <ScanText size={16} color="#1976d2" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  Tipo di analisi
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  L’AI legge il testo della nota e restituisce: esito, impatto rischio, qualità del
                  contenuto e una sintesi breve. Non inventa dettagli non presenti nel testo.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'warning.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.2
                }}
              >
                <Layers size={16} color="#ed6c02" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  Come viene eseguita
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Le note selezionate vengono elaborate in batch (10 alla volta). Durante la run vedi
                  l’avanzamento (batch processati e note analizzate).
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'success.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.2
                }}
              >
                <Database size={16} color="#2e7d32" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  Salvataggio risultati
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  I risultati vengono salvati nel database principale nella tabella nota_ai_analysis,
                  legati alla singola nota.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Selezionate: {selectedNoteIds.length}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Solo note referente legale: {onlyLegalNotes ? 'Sì' : 'No'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setRunInfoOpen(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setRunInfoOpen(false);
              await handleRunAnalysis();
            }}
            disabled={loading || runInProgress}
          >
            Avvia analisi
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <AiNotesLogoIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Analisi AI note
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Analisi qualitativa delle note lavorate nel periodo selezionato. I risultati AI sono
            salvati nel database principale nella tabella nota_ai_analysis.
          </Typography>
        </Box>
      </Box>

      <StyledCard sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Da"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="A"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={legalOptions}
                getOptionLabel={(option) =>
                  [option.name, option.surname].filter(Boolean).join(' ') || ''
                }
                value={
                  legalOptions.find((u) => String(u.id) === String(selectedLegal)) || null
                }
                onChange={(event, newValue) => {
                  setSelectedLegal(newValue ? String(newValue.id) : '');
                }}
                disabled={!isAdmin}
                disableClearable={!isAdmin}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Referente legale"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onlyLegalNotes}
                    onChange={(event) => setOnlyLegalNotes(event.target.checked)}
                  />
                }
                label="Solo note referente legale"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleApply}
                disabled={loading}
                startIcon={<RefreshCw size={16} />}
              >
                Applica
              </Button>
            </Grid>
            {tab === 'notes' && (
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleOpenRunInfo}
                  disabled={loading || runInProgress}
                  startIcon={<RefreshCw size={16} />}
                >
                  {runInProgress ? 'Analisi in corso...' : 'Analizza AI'}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Tabs
            value={tab}
            onChange={(event, newValue) => setTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab value="notes" label="Note" />
            <Tab value="analyzed" label="Note analizzate" />
            <Tab value="summary" label="Riepilogo AI" />
          </Tabs>

          {tab === 'notes' && (
            <>
              <Box
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center'
                }}
              >
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="notes-tab-analysis-presence-label">
                    Stato analisi (tab Note)
                  </InputLabel>
                  <Select
                    labelId="notes-tab-analysis-presence-label"
                    value={notesTabAnalysisPresenceFilter}
                    label="Stato analisi (tab Note)"
                    onChange={(event) =>
                      setNotesTabAnalysisPresenceFilter(event.target.value)
                    }
                  >
                    <MenuItem value="without">Solo non analizzate</MenuItem>
                    <MenuItem value="all">Tutte le note</MenuItem>
                    <MenuItem value="with">Solo già analizzate</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip
                  title="Stato AI nella tab Note: punto verde = nota già analizzata; punto arancione = nota non ancora analizzata."
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                  />
                </Tooltip>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  In questa pagina: {notesWithoutAnalysisCount} non analizzate,{' '}
                  {notesWithAnalysisCount} già analizzate.
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Seleziona le note che vuoi analizzare con AI nel periodo corrente e premi
                &quot;Analizza AI&quot;.
              </Typography>
              <Box
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: 'success.main'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Già analizzata
                  </Typography>
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: 'warning.main'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Non analizzata
                  </Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          indeterminate={
                            selectedNoteIds.length > 0 &&
                            selectedNoteIds.length < filteredNotesForSelectionTab.length
                          }
                          checked={
                            filteredNotesForSelectionTab.length > 0 &&
                            filteredNotesForSelectionTab.every((n) =>
                              selectedNoteIds.includes(n.note_id)
                            )
                          }
                          onChange={handleToggleSelectAllCurrentPage}
                        />
                      </TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Pratica</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Controparte</TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell>Autore</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <span>Stato AI</span>
                          <Tooltip
                            title="Punto verde: analisi AI già presente. Punto arancione: nota da analizzare."
                            arrow
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 16, color: 'text.disabled', cursor: 'default' }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>Testo nota</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredNotesForSelectionTab.map((row) => {
                      const author =
                        [row.user_name, row.user_surname].filter(Boolean).join(' ') ||
                        (row.rif_utente ? `ID: ${row.rif_utente}` : '-');
                      const checked = selectedNoteIds.includes(row.note_id);
                      const hasAnalysis = !!row.analysis;
                      return (
                        <TableRow
                          key={row.note_id}
                          hover
                          onClick={() => handleToggleNoteSelection(row.note_id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => handleToggleNoteSelection(row.note_id)}
                              onClick={(event) => event.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString('it-IT')
                              : '-'}
                          </TableCell>
                          <TableCell>{row.pratica_reference || row.negotiation_id}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {row.customer_full_name || '-'}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {row.customer_email && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      color: 'text.secondary',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    <Mail size={12} />
                                    {row.customer_email}
                                  </Box>
                                )}
                                {row.customer_telephone && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      color: 'text.secondary',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    <Phone size={12} />
                                    {row.customer_telephone}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{row.controparte || '-'}</TableCell>
                          <TableCell>{row.category_name || '-'}</TableCell>
                          <TableCell>{author}</TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                hasAnalysis
                                  ? 'Nota già analizzata con AI'
                                  : 'Nota non ancora analizzata con AI'
                              }
                              arrow
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  border: '1px solid',
                                  borderColor: hasAnalysis ? 'success.main' : 'warning.main',
                                  bgcolor: hasAnalysis ? 'success.50' : 'warning.50'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    bgcolor: hasAnalysis ? 'success.main' : 'warning.main'
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {row.descrizione ? (
                              <Tooltip title={stripHtml(row.descrizione)} arrow>
                                <span>
                                  {stripHtml(row.descrizione).slice(0, 160)}
                                  {stripHtml(row.descrizione).length > 160 ? '…' : ''}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === 'analyzed' && (
            <React.Fragment>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="analysis-presence-label">Presenza analisi</InputLabel>
                  <Select
                    labelId="analysis-presence-label"
                    value={analysisPresenceFilter}
                    label="Presenza analisi"
                    onChange={(event) => setAnalysisPresenceFilter(event.target.value)}
                  >
                    <MenuItem value="all">Tutte le note</MenuItem>
                    <MenuItem value="with">Solo con analisi AI</MenuItem>
                    <MenuItem value="without">Solo senza analisi AI</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="risk-filter-label">Rischio</InputLabel>
                    <Select
                      labelId="risk-filter-label"
                      value={riskFilter}
                      label="Rischio"
                      onChange={(event) => setRiskFilter(event.target.value)}
                    >
                      <MenuItem value="all">Tutti i livelli</MenuItem>
                      <MenuItem value="alto">Alto</MenuItem>
                      <MenuItem value="medio">Medio</MenuItem>
                      <MenuItem value="basso">Basso</MenuItem>
                      <MenuItem value="non_rilevabile">Non rilevabile</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip
                    title="Il rischio indica l’impatto potenziale della nota sulla rischiosità della pratica: 'alto' = segnali di forte criticità, 'medio' = possibili problemi che richiedono attenzione, 'basso' = nota di routine senza impatto rilevante, 'non rilevabile' = dal testo non è possibile stimare il rischio."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                    />
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="quality-filter-label">Qualità nota</InputLabel>
                    <Select
                      labelId="quality-filter-label"
                      value={qualityFilter}
                      label="Qualità nota"
                      onChange={(event) => setQualityFilter(event.target.value)}
                    >
                      <MenuItem value="all">Tutte le qualità</MenuItem>
                      <MenuItem value="molto_bassa">Molto bassa</MenuItem>
                      <MenuItem value="bassa">Bassa</MenuItem>
                      <MenuItem value="media">Media</MenuItem>
                      <MenuItem value="alta">Alta</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip
                    title="La qualità misura quanto la nota è chiara, completa e utile per capire lo stato della pratica: 'molto bassa' = testo poco utile o confuso, 'bassa' = informazioni minime, 'media' = nota sufficientemente chiara, 'alta' = nota ben strutturata e informativa."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                    />
                  </Tooltip>
                </Box>
              </Box>

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Vengono mostrate le note del periodo selezionato con eventuali analisi AI
                salvate nel database.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" align="center">
                        AI
                      </TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Pratica</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Controparte</TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell>Autore</TableCell>
                      <TableCell>Testo nota</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAnalyzedNotes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nessuna nota trovata con i filtri correnti nella pagina attuale.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAnalyzedNotes.map((row) => {
                      const author =
                        [row.user_name, row.user_surname].filter(Boolean).join(' ') ||
                        (row.rif_utente ? `ID: ${row.rif_utente}` : '-');
                      const analysis = row.analysis || null;

                      const riskColor =
                        analysis && analysis.impatto_rischio === 'alto'
                          ? 'error'
                          : analysis && analysis.impatto_rischio === 'medio'
                          ? 'warning'
                          : analysis && analysis.impatto_rischio === 'basso'
                          ? 'success'
                          : 'default';

                      return (
                        <React.Fragment key={row.note_id}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              {analysis && (
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleToggleAnalysisRow(row.note_id);
                                  }}
                                  color={
                                    riskColor !== 'default' ? riskColor : 'primary'
                                  }
                                >
                                  <AiNotesLogoIcon size={16} />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.created_at
                                ? new Date(row.created_at).toLocaleString('it-IT')
                                : '-'}
                            </TableCell>
                            <TableCell>{row.pratica_reference || row.negotiation_id}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {row.customer_full_name || '-'}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  {row.customer_email && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <Mail size={12} />
                                      {row.customer_email}
                                    </Box>
                                  )}
                                  {row.customer_telephone && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <Phone size={12} />
                                      {row.customer_telephone}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>{row.controparte || '-'}</TableCell>
                            <TableCell>{row.category_name || '-'}</TableCell>
                            <TableCell>{author}</TableCell>
                            <TableCell>
                              {row.descrizione ? (
                                <Tooltip title={stripHtml(row.descrizione)} arrow>
                                  <span>
                                    {stripHtml(row.descrizione).slice(0, 160)}
                                    {stripHtml(row.descrizione).length > 160 ? '…' : ''}
                                  </span>
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              sx={{ paddingBottom: 0, paddingTop: 0 }}
                            >
                              <Collapse
                                in={expandedAnalysisRow === row.note_id && !!analysis}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box
                                  sx={{
                                    margin: 1,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.default'
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Tipo contenuto</TableCell>
                                        <TableCell>Esito</TableCell>
                                        <TableCell>Rischio</TableCell>
                                        <TableCell>Qualità nota</TableCell>
                                        <TableCell>Sintesi breve</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell>{analysis?.tipo_contenuto || '-'}</TableCell>
                                        <TableCell>{analysis?.esito || '-'}</TableCell>
                                        <TableCell>
                                          {analysis?.impatto_rischio ? (
                                            <Chip
                                              size="small"
                                              label={`Rischio: ${analysis.impatto_rischio}`}
                                              color={riskColor}
                                              variant={
                                                riskColor === 'default' ? 'outlined' : 'filled'
                                              }
                                            />
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                        <TableCell>{analysis?.qualita_nota || '-'}</TableCell>
                                        <TableCell>
                                          {analysis?.sintesi_breve || '-'}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </React.Fragment>
          )}

          {tab === 'summary' && (
            <React.Fragment>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Riepilogo delle analisi AI per il periodo selezionato
                {selectedLegal
                  ? ' e il referente legale scelto.'
                  : ' su tutte le note analizzate nel periodo.'}
              </Typography>
              {summaryLoading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Caricamento riepilogo in corso...
                </Typography>
              ) : !summaryData || summaryData.totalAnalyzed === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nessuna nota con analisi AI trovata nel periodo e filtri selezionati.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="subtitle2">Esito analisi</Typography>
                      <Tooltip
                        title="L’esito sintetizza l’effetto della nota sulla pratica: 'positivo' = passo avanti o miglioramento, 'neutro' = aggiornamento senza cambi di scenario, 'negativo' = peggioramento o nuova criticità, 'non_applicabile' = la nota non contiene un esito chiaro."
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 260, mt: 1 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            dataKey="count"
                            data={summaryData.byOutcome}
                            nameKey="value"
                            outerRadius={80}
                            label
                          >
                            {summaryData.byOutcome.map((entry, index) => {
                              const colors = ['#22c55e', '#64748b', '#ef4444', '#9ca3af'];
                              const color = colors[index % colors.length];
                              return <Cell key={entry.value || index} fill={color} />;
                            })}
                          </Pie>
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                    >
                      Mostra quante note analizzate risultano con esito positivo, neutro, negativo
                      o non applicabile nel periodo selezionato.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="subtitle2">Rischio</Typography>
                      <Tooltip
                        title="Mostra la distribuzione delle note per impatto sul rischio della pratica: 'alto', 'medio', 'basso' o 'non rilevabile'."
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 260, mt: 1 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={summaryData.byRisk}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="value" />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip />
                          <Bar dataKey="count">
                            {summaryData.byRisk.map((entry, index) => {
                              const map = {
                                alto: '#ef4444',
                                medio: '#f97316',
                                basso: '#22c55e',
                                non_rilevabile: '#9ca3af',
                                non_impostato: '#cbd5f5'
                              };
                              const color = map[entry.value] || '#6366f1';
                              return <Cell key={entry.value || index} fill={color} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                    >
                      Indica quante note rientrano nei diversi livelli di impatto sul rischio:
                      alto, medio, basso o non rilevabile.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="subtitle2">Qualità note</Typography>
                      <Tooltip
                        title="La qualità misura quanto la nota è chiara, completa e utile: 'molto bassa' = testo poco utile o confuso, 'bassa' = informazioni minime, 'media' = nota sufficientemente chiara, 'alta' = nota ben strutturata e informativa."
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18, color: 'text.disabled', cursor: 'default' }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 260, mt: 1 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={summaryData.byQuality}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="value" />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip />
                          <Bar dataKey="count">
                            {summaryData.byQuality.map((entry, index) => {
                              const map = {
                                molto_bassa: '#ef4444',
                                bassa: '#f97316',
                                media: '#3b82f6',
                                alta: '#22c55e',
                                non_impostato: '#9ca3af'
                              };
                              const color = map[entry.value] || '#6366f1';
                              return <Cell key={entry.value || index} fill={color} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                    >
                      Mostra quante note hanno una qualità testuale molto bassa, bassa, media o
                      alta secondo la valutazione AI.
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </React.Fragment>
          )}

          {tab !== 'summary' && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Box>
      </StyledCard>
    </Box>
  );
}
